import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthGuard = ({ children, requiredAuth = true, redirectTo = '/login-register', requireAdmin = false }) => {
  const { user, loading, isAuthenticated, isAdmin, initialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Wait until auth is fully initialized AND not loading before making decisions
    if (loading || !initialized) return;

    // If authentication is required but user is not authenticated
    if (requiredAuth && !isAuthenticated) {
      navigate(redirectTo, { replace: true, state: { from: location } });
      return;
    }

    // If admin is required: only redirect if we have a loaded profile and user is NOT admin
    // This prevents redirecting when profile is still being fetched
    if (requiredAuth && requireAdmin && !isAdmin) {
      navigate('/dashboard-overview', { replace: true });
      return;
    }

    // If user is authenticated but shouldn't be (like on login page)
    if (!requiredAuth && isAuthenticated) {
      navigate('/dashboard-overview', { replace: true });
      return;
    }
  }, [user, loading, initialized, isAuthenticated, isAdmin, requiredAuth, requireAdmin, navigate, redirectTo, location]);

  // Show loading spinner while auth state is being determined
  if (loading || !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-onBackground/70">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return children;
};

export default AuthGuard;